import { RefObject } from "react";
import { AgGridReact } from "ag-grid-react";
import useUiuxStore from "src/store/useUiuxStore";
import { showWarning } from "src/utils/toast/toastUtil";
import { validateItemAccount, isDuplicateChildItemcd, restoreOriginalValues } from "../Util_KSK2010_R01";
import { KSK2010_R01Dto } from "../Dto_KSK2010_R01";
import { focusToCell } from "src/utils/grid/GridHandlerContent";

interface UseKSK2010_R01ModalProps {
    updateKSK2010_R01List: (index: number, data: KSK2010_R01Dto) => void;
    gridRef: RefObject<AgGridReact<KSK2010_R01Dto> | null>;
    KSK2010_R01List: KSK2010_R01Dto[];
}

// 코드 검색 모달 확인 데이터 타입
interface ItemConfirmData {
    itemCd?: string;
    itemNm?: string;
    itemSpec?: string;
    itemUnit?: string;
}

interface EmployeeConfirmData {
    empNo?: string;
    empNm?: string;
}

type CodeSearchConfirmData = ItemConfirmData & EmployeeConfirmData;

export function useKSK2010_R01Modal({ updateKSK2010_R01List, gridRef, KSK2010_R01List }: UseKSK2010_R01ModalProps) {
    const { setCodeSearchModalProps } = useUiuxStore();

    // 모달 닫힘 콜백
    const handlePopupClose = () => {
        if (gridRef.current?.api) {
        gridRef.current.api.stopEditing();
        }
    };

    // onClear 핸들러 맵: 모달에서 비우기 버튼 클릭 시 해당 필드 값을 빈 값으로 초기화하고 모달을 닫음
    const clearHandlers: Record<string, (valueIndex: number, originalData: KSK2010_R01Dto) => void> = {
		//품목코드 
        itemCd: (valueIndex: number, originalData: KSK2010_R01Dto) => {
            updateKSK2010_R01List(valueIndex, {
                ...originalData,
                itemCd: '',
                itemNm: '',
                itemSpec: '',
                itemUnit: '',
                isModified: false
            });
        },
		//작업자 
        orderEmp: (valueIndex: number, originalData: KSK2010_R01Dto) => {
            updateKSK2010_R01List(valueIndex, {
                ...originalData,
                orderEmp: '',
                orderEmpNm: '',
                isModified: false
            });
        }
    };

    // onConfirm 핸들러 맵 : 모달에서 선택한 데이터를 그리드 행에 반영하고 다음 셀로 포커스를 이동 
    const confirmHandlers: Record<string, (data: CodeSearchConfirmData, valueIndex: number, originalData: KSK2010_R01Dto) => void> = {
		//품목 코드 
        itemCd: (data: CodeSearchConfirmData, valueIndex: number, originalData: KSK2010_R01Dto) => {
            updateKSK2010_R01List(valueIndex, {
                ...originalData,
                itemCd: data.itemCd || '',
                itemNm: data.itemNm || '',
                itemSpec: data.itemSpec || '',
                itemUnit: data.itemUnit || '',
                isModified: originalData.inserted ? false : true, // 신규행이면 false, 기존행이면 true
            });
            // 품목 선택 후 수량으로 포커스 이동
            focusToCell<KSK2010_R01Dto>(gridRef, valueIndex, 'orderQty');
        },
		// 작업자 
        orderEmp: (data: CodeSearchConfirmData, valueIndex: number, originalData: KSK2010_R01Dto) => {
            updateKSK2010_R01List(valueIndex, {
                ...originalData,
                orderEmp: data.empNo || '',
                orderEmpNm: data.empNm || '',
                isModified: originalData.inserted ? false : true, // 신규행이면 false, 기존행이면 true
            });
            // 작업자 선택 후 품목코드로 포커스 이동
            focusToCell<KSK2010_R01Dto>(gridRef, valueIndex, 'itemCd');
        }
    };

    // 모달 옵션 맵
    const modalOptions: Record<string, { size: 'md' | 'half'; width: number }> = {
        itemCd: { size: 'md', width: 50 },
        orderEmp: { size: 'half', width: 20 }
    };

  // 코드검색 모달 열기 및 처리
    const settingCodeSearchModalProps = (
        category: string,
        search: string,
        isInfo: boolean,
        handleFoucsId: string,
        originalData: KSK2010_R01Dto,
        valueIndex: number,
        // extraFilter?: { itemAccount?: string }
    ) => {
        setCodeSearchModalProps({
			category,
			search,
			isInfo,
			handleFoucsId,
			onClear: () => {
				const clearHandler = clearHandlers[handleFoucsId];
				if (clearHandler) {
					clearHandler(valueIndex, originalData);
				}
				handlePopupClose();
			},
			onConfirm: (data: CodeSearchConfirmData) => {
				const confirmHandler = confirmHandlers[handleFoucsId];
				if (confirmHandler) {
					confirmHandler(data, valueIndex, originalData);
				}
			},
			wrapperOption: {
				...modalOptions[handleFoucsId],
				overlay: true
			},
			// extraFilter
        });
    };

    return { settingCodeSearchModalProps, handlePopupClose };
} 